<template>
    <div v-if="!loading" class="relative">
        <BackLink @go-back="handleGoBack" />
        <div class="col-span-12 absolute top-0 right-0">
            <a
                href="#"
                @click.prevent="setShowAffiliateModal(true)"
                class="text-orange text-xs hover:text-orange-400 focus:text-slate-500 focus:outline-slate-300 rounded"
            >
                Affiliate Disclosure
            </a>
        </div>

        <!-- Hotel image and map -->
        <div class="property-details relative pb-16">
            <section class="hotel-details-wrapper grid grid-cols-12 gap-4">
                <div class="hotel-details col-span-12 lg:col-span-8">
                    <HeroImages />
                </div>

                <div
                    class="hidden lg:block hotel-details-sidebar col-span-12 min-h-44 md:col-span-4"
                >
                    <SmallMap />
                </div>
            </section>
            <!-- LG MAP PLACEHOLDER -->
            <div class="fixed inset-0 z-[999]" v-if="showOnMap">
                <Suspense>
                    <MapResults />
                </Suspense>
            </div>

            <!-- Property Details Section -->
            <div class="grid grid-cols-12 gap-4 items-center">
                <div class="col-span-4 flex justify-end"></div>
            </div>
            <div
                class="pt-10 bg-slate-100 justify-between flex flex-col md:flex-row"
            >
                <div class="w-full flex-col justify-start items-start mb-5">
                    <div>
                        <h1
                            class="text-slate-800 text-xl md:text-3xl font-medium pr-2 mb-5"
                            v-html="property.name"
                        ></h1>
                        <p class="text-slate-500 text-sm">
                            {{ propertyAddress }}
                        </p>
                    </div>
                    <div class="pt-6 gap-6 flex text-slate-800 text-sm">
                        <div
                            class="gap-2 flex items-center justify-center underline hover:text-slate-600"
                        >
                            <Icon icon="globe" svgClass="w-5 h-5 fill-none" />
                            <a
                                :href="property.url"
                                target="_blank"
                                class="focus:text-slate-500 focus:outline-slate-300 rounded"
                            >
                                Hotel website
                            </a>
                        </div>
                        <div class="gap-2 flex items-center justify-center">
                            <AlertsCount />
                        </div>
                    </div>
                </div>
                <div class="md:justify-center gap-3 flex mb-5">
                    <div class="h-10 w-10">
                        <Fav
                            :isWidget="false"
                            :indie="true"
                            :property="property"
                            shape="square"
                        />
                    </div>
                    <CreateAlertButton :item="property" />
                </div>
            </div>
        </div>

        <!-- ALERTS WIDGET -->
        <AlertsWidget
            view="property"
            title="Availability alerts"
            description="Your active alerts for this property."
        />

        <!-- PROPERTY SEARCH -->
        <div class="col-span-full mb-5">
            <PropertySearch />
        </div>

        <div class="property-details relative">
            <section
                class="hotel-availability-wrapper bg-white overflow-visible shadow ring-1 ring-black ring-opacity-5 rounded-lg mb-4"
            >
                <AvailabilityCalendar />
            </section>
        </div>
        <BookingDetails />
    </div>
    <div v-else>
        <LoadingState :loading="loading" />
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useAlertsStore } from '@/store/alerts';
import { useAppStore } from '@/store/app';
import { useHotelsStore } from '@/store/hotels';
import { useCalendarStore } from '@/store/calendar';
import { usePointsStore } from '@/store/points';
import CreateAlertButton from '@/components/alerts/create-alert-button.vue';
import HeroImages from '@/components/hotels/property/hero-images.vue';
import BookingDetails from '@/components/hotels/property/booking-details/booking-details.vue';
import PropertySearch from '@/components/hotels/property/property-search.vue';
import AvailabilityCalendar from '@/components/calendar/index.vue';
import Fav from '@/components/hotels/property/fav.vue';
import Icon from '@/components/ui/icon/icon.vue';
import AlertsCount from '@/components/alerts/alerts-count/alerts-count.vue';
import SmallMap from '@/components/hotels/property/small-map/small-map.vue';
import MapResults from '@/components/hotels/search/map-results/map-results.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import AlertsWidget from '@/components/alerts/alerts-widget/alerts-widget.vue';
import BackLink from '@/components/ui/back-link/ui-back-link.vue';
import Filters from '@/filters';

export default {
    name: 'HotelProperty',
    components: {
        AlertsCount,
        AlertsWidget,
        AvailabilityCalendar,
        BackLink,
        BookingDetails,
        CreateAlertButton,
        Fav,
        HeroImages,
        Icon,
        LoadingState,
        MapResults,
        PropertySearch,
        SmallMap,
    },
    data: () => ({
        checkIn: null,
        checkOut: null,
        // isBeforeRouteEnterCalled: false,
    }),
    computed: {
        ...mapState(useAlertsStore, ['alertsViewFilters']),
        ...mapState(useHotelsStore, [
            'loading',
            'property',
            'calConfig',
            'slug',
            'searchQuery',
            'showOnMap',
        ]),
        propertyAddress() {
            return Filters.formatAddress(this.property);
        },
        lastRouteLabel() {
            const lastRoute = localStorage.getItem('last_route');
            switch (lastRoute) {
                case 'Hotels':
                    return 'Hotels Home';
                case 'HotelSearch':
                    return 'Search Results';
                case 'HotelProperty':
                    return 'Property';
                case 'Favorites':
                    return 'Favorites';
                case 'Alerts':
                    return 'Alerts';
                default:
                    return 'Previous';
            }
        },
    },
    methods: {
        ...mapActions(useAlertsStore, [
            'replaceSelectedProperties',
            'setActiveProperty',
        ]),
        ...mapActions(useAppStore, ['setShowAffiliateModal']),
        ...mapActions(useHotelsStore, [
            'setLoading',
            'getProperty',
            'setProperty',
            'setShowOnMap',
        ]),
        ...mapActions(useCalendarStore, [
            'getEvents',
            'setSlug',
            'setDateRange',
            'setUrl',
            'setView',
            'startDate',
            'endDate',
            'setTab',
        ]),
        ...mapActions(usePointsStore, ['getRewards']),
        ...mapActions(useAppStore, ['setBreadcrumbs', 'previousRoute']),
        handleGoBack() {
            // this.setProperty({}); //need this?
            // this.setActiveProperty(null);
            this.replaceSelectedProperties(this.alertsViewFilters);
        },
    },
    mounted() {
        this.setLoading(true);
        this.setActiveProperty(this.property);
        this.checkIn = this.$route.query.check_in
            ? this.$route.query.check_in
            : null;
        this.checkOut = this.$route.query.check_out
            ? this.$route.query.check_out
            : null;
        this.setSlug(this.$route.params.slug);

        this.setUrl(
            `/hotels/properties/${this.$route.params.slug}/availability`
        );

        this.setDateRange({ startDate: this.checkIn, endDate: this.checkOut });

        if (!this.checkIn || !this.checkOut) {
            this.setTab('calendar');
        } else {
            this.setTab('list');
        }

        this.getProperty(this.$route.params.slug).then(() => {
            if (import.meta.env.VITE_APP_ENV === 'production') {
                woopra.track('property-view', {
                    propertyname: this.property.name,
                    id: this.property.id,
                    uuid: localStorage.getItem('uuid'),
                });
            }

            this.setBreadcrumbs('slug', this.property.name);

            document.title = new DOMParser().parseFromString(
                `${this.property.name} | My10x`,
                'text/html'
            ).documentElement.textContent;
        });

        this.getRewards();
    },
};
</script>
